import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MapPin, Phone, Mail, Send } from "lucide-react";
import { toast } from "sonner";

export default function ContactSection({ embedded = false }) {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            toast.success("Message sent", { description: "We'll get back to you shortly." });
            setForm({ name: "", email: "", message: "" });
        } catch (err) {
            toast.error("Could not send", {
                description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const body = (
        <div className="grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-50 text-accent-600 text-xs font-semibold tracking-wide uppercase">
                        Get in Touch
                    </span>
                    <h2 className="mt-5 text-4xl md:text-5xl font-heading font-semibold tracking-tight text-ink-900">
                        Visit our studio
                    </h2>
                    <p className="mt-4 text-base md:text-lg text-ink-700 leading-relaxed">
                        Drop in for a class, ask about a course, or just say hi. We're
                        always happy to hear from fellow yogis.
                    </p>

                    <ul className="mt-8 space-y-5">
                        <li className="flex items-start gap-4">
                            <span className="h-11 w-11 rounded-2xl bg-white shadow-soft border border-gray-100 flex items-center justify-center text-brand-600">
                                <MapPin className="h-5 w-5" strokeWidth={1.6} />
                            </span>
                            <div>
                                <div className="text-xs uppercase tracking-wider text-ink-400">
                                    Studio Location
                                </div>
                                <div className="font-medium text-ink-900">
                                    Aham Yog Institute
                                    <br />
                                    Mumbai, Maharashtra, India
                                </div>
                            </div>
                        </li>
                        <li className="flex items-start gap-4">
                            <span className="h-11 w-11 rounded-2xl bg-white shadow-soft border border-gray-100 flex items-center justify-center text-brand-600">
                                <Phone className="h-5 w-5" strokeWidth={1.6} />
                            </span>
                            <div>
                                <div className="text-xs uppercase tracking-wider text-ink-400">
                                    Phone
                                </div>
                                <a
                                    href="tel:+910000000000"
                                    className="font-medium text-ink-900 hover:text-brand-600"
                                >
                                    +91 00000 00000
                                </a>
                            </div>
                        </li>
                        <li className="flex items-start gap-4">
                            <span className="h-11 w-11 rounded-2xl bg-white shadow-soft border border-gray-100 flex items-center justify-center text-brand-600">
                                <Mail className="h-5 w-5" strokeWidth={1.6} />
                            </span>
                            <div>
                                <div className="text-xs uppercase tracking-wider text-ink-400">
                                    Email
                                </div>
                                <a
                                    href="mailto:hello@ahamyog.com"
                                    className="font-medium text-ink-900 hover:text-brand-600"
                                >
                                    hello@ahamyog.com
                                </a>
                            </div>
                        </li>
                    </ul>
                </div>

                <form
                    onSubmit={onSubmit}
                    className="lg:col-span-7 bg-white rounded-3xl shadow-soft border border-gray-100 p-6 md:p-10 space-y-5"
                    data-testid="contact-form"
                >
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="contact-name" className="text-sm">
                                Your name
                            </Label>
                            <Input
                                id="contact-name"
                                name="name"
                                value={form.name}
                                onChange={onChange}
                                required
                                className="mt-1.5 rounded-xl"
                                data-testid="contact-name-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="contact-email" className="text-sm">
                                Email
                            </Label>
                            <Input
                                id="contact-email"
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={onChange}
                                required
                                className="mt-1.5 rounded-xl"
                                data-testid="contact-email-input"
                            />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="contact-message" className="text-sm">
                            Your message
                        </Label>
                        <Textarea
                            id="contact-message"
                            name="message"
                            value={form.message}
                            onChange={onChange}
                            required
                            rows={5}
                            className="mt-1.5 rounded-xl"
                            data-testid="contact-message-input"
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={submitting}
                        className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta px-6 py-5 text-base"
                        data-testid="contact-submit-btn"
                    >
                        <Send className="h-4 w-4 mr-1.5" />
                        {submitting ? "Sending..." : "Send Message"}
                    </Button>
                </form>
            </div>
        );

    if (embedded) {
        return <div data-testid="contact-section-embedded">{body}</div>;
    }
    return (
        <section
            id="contact"
            className="py-20 md:py-28 bg-paper-alt"
            data-testid="contact-section"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {body}
            </div>
        </section>
    );
}
